import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Stat, StatGroup, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Scheduled work, and whether it is happening.
 *
 * This page exists because of what its absence cost: `ensure_cdr_partitions_ahead`
 * was called once, by the migration that created it, three months of CDR
 * partitions were made, and nothing was ever going to make more. The horizon
 * ended on 2027-01-01 and the only way to find that out was to go looking.
 *
 * So the design rule here is that a job which stops must be VISIBLE, not merely
 * absent. "Never run" and "ran and had nothing to do" are rendered as different
 * things, and whether the scheduler itself is turned on is stated at the top —
 * without that, a page of jobs all showing as due reads like a backlog when the
 * truth is that nothing is turning the handle.
 */

interface JobRow {
  key: string; description: string; every_minutes: number; is_enabled: boolean;
  /** Report only: counts what it would delete and changes nothing (0089). */
  dry_run: boolean;
  next_run_at: string; running_since: string | null;
  last_run_at: string | null; last_status: string | null; last_error: string | null;
  last_duration_ms: number | null; consecutive_failures: number; overdue_sec: number;
}
interface RunRow {
  job_key: string; started_at: string; status: string;
  duration_ms: number | null; detail: Record<string, unknown> | null; error: string | null;
}
interface Payload { scheduler_enabled: boolean; data: JobRow[]; recent: RunRow[] }

/** "every 1440 minutes" is not how anybody thinks about a daily job. */
function cadence(minutes: number): string {
  if (minutes % 1440 === 0) {
    const d = minutes / 1440;
    return d === 1 ? 'Daily' : `Every ${d} days`;
  }
  if (minutes % 60 === 0) {
    const h = minutes / 60;
    return h === 1 ? 'Hourly' : `Every ${h} hours`;
  }
  return `Every ${minutes} min`;
}

export function JobsPage() {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const step = useStepUp();
  const [expanded, setExpanded] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['platform-jobs'],
    queryFn: () => api.get<Payload>('/v1/platform/jobs'),
    // A job page that does not move is indistinguishable from a job page that
    // is broken. Ten seconds is enough to watch a "Run now" land.
    refetchInterval: 10_000,
  });

  const runNow = useMutation({
    mutationFn: (key: string) => api.post(`/v1/platform/jobs/${key}/actions/run`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform-jobs'] }),
  });
  const setEnabled = useMutation({
    mutationFn: (v: { key: string; is_enabled: boolean }) =>
      api.patch(`/v1/platform/jobs/${v.key}`, { is_enabled: v.is_enabled }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform-jobs'] }),
  });
  const setDryRun = useMutation({
    mutationFn: (v: { key: string; dry_run: boolean }) =>
      api.patch(`/v1/platform/jobs/${v.key}`, { dry_run: v.dry_run }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform-jobs'] }),
  });

  if (q.error) return <ErrorBanner error={q.error} />;

  const jobs = q.data?.data ?? [];
  const runs = q.data?.recent ?? [];
  const failing = jobs.filter((j) => j.consecutive_failures > 0);
  const neverRan = jobs.filter((j) => !j.last_run_at);
  const mayCommand = can('node:command');

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Scheduled jobs</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Maintenance the platform runs on its own, and whether it is running.
          </p>
        </div>
      </div>

      {/*
        The scheduler being OFF is the failure this page most needs to show, and
        it is the state the platform was in for its entire history. Every job
        would read as merely overdue.
      */}
      {q.data && !q.data.scheduler_enabled && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            The scheduler is switched off on this deployment, so nothing below
            will run whatever it says it is due. ARC_SCHEDULER is not set.
          </span>
        </div>
      )}

      {failing.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            {failing.map((j) => `${j.key} has failed ${j.consecutive_failures} time${j.consecutive_failures === 1 ? '' : 's'} in a row`).join('; ')}.
          </span>
        </div>
      )}

      <StepUpPrompt step={step} what="Changing a scheduled job">
        Switching one off, or letting one delete for real, is confirmed with your password.
      </StepUpPrompt>

      {jobs.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <StatGroup>
            <Stat bare icon="clock" hue={1} label="Jobs" value={jobs.length}
              sub={`${jobs.filter((j) => j.is_enabled).length} enabled`} />
            <Stat bare icon="warning" hue={3} label="Failing" value={failing.length}
              tone={failing.length > 0 ? 'danger' : undefined}
              sub={failing.length > 0 ? 'consecutive failures' : 'all healthy'} />
            {/* Never-run is its own number. It is the state that produced the
                partition cliff, and it hides inside "no failures". */}
            <Stat bare icon="server" hue={4} label="Never run" value={neverRan.length}
              sub={neverRan.length > 0 ? 'no record of a run' : 'all have run'} />
          </StatGroup>
        </div>
      )}

      <div className="arc-card">
        {q.data === undefined ? <TableSkeleton rows={3} cols={5} /> : jobs.length === 0 ? (
          <Empty icon="clock" title="No jobs registered"
            hint="Jobs are seeded by migration; none are present." />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table arc-table-fixed">
              <colgroup>
                <col style={{ width: 210 }} />
                <col />
                <col style={{ width: 110 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 270 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Job</th><th>What it does</th><th>Runs</th>
                  <th>Last run</th><th>Next</th><th />
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const failed = j.last_status === 'failed';
                  const open = expanded === j.key;
                  return (
                    <>
                      <tr key={j.key} className="arc-clickable"
                        onClick={() => setExpanded(open ? null : j.key)}>
                        <td>
                          <span className="arc-mono arc-truncate"
                            style={{ fontWeight: 550, opacity: j.is_enabled ? 1 : 0.55 }}>
                            {j.key}
                          </span>
                          {j.dry_run && <div style={{ marginTop: 3 }}><Badge tone="warn">report only</Badge></div>}
                        </td>
                        <td className="arc-muted arc-truncate" title={j.description}>
                          {j.description}
                        </td>
                        <td className="arc-muted">{cadence(j.every_minutes)}</td>
                        <td className="arc-truncate">
                          {j.running_since ? (
                            <span style={{ color: 'var(--arc-accent)' }}>running now</span>
                          ) : !j.last_run_at ? (
                            /* Not the same as a failure, and not the same as
                               success: the platform has no record of this job
                               ever having run. */
                            <span style={{ color: 'var(--arc-warn)' }}>never run</span>
                          ) : (
                            <span style={failed ? { color: 'var(--arc-danger)', fontWeight: 550 } : undefined}
                              title={new Date(j.last_run_at).toLocaleString('en-ZA')}>
                              {failed ? 'failed ' : ''}{relativeTime(j.last_run_at)}
                            </span>
                          )}
                        </td>
                        <td className="arc-muted arc-truncate">
                          {!j.is_enabled ? <span className="arc-subtle">disabled</span>
                            : j.overdue_sec > 120
                              ? <span style={{ color: 'var(--arc-warn)' }}>
                                  overdue {Math.round(j.overdue_sec / 60)}m
                                </span>
                              : relativeTime(j.next_run_at)}
                        </td>
                        <td className="arc-td-right" onClick={(e) => e.stopPropagation()}>
                          {mayCommand && (
                            <span className="arc-row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                                disabled={!j.is_enabled || runNow.isPending}
                                onClick={() => runNow.mutate(j.key)}>
                                Run now
                              </button>
                              {j.dry_run && (
                                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-btn-danger-ghost"
                                  disabled={setDryRun.isPending}
                                  title="It has been reporting what it would delete: see its recent runs"
                                  onClick={() => {
                                    if (confirm(`Let ${j.key} delete for real? It has been reporting what it would delete; from its next run it deletes it, and that cannot be undone.`)) {
                                      void step.run(() => setDryRun.mutateAsync({ key: j.key, dry_run: false }));
                                    }
                                  }}>
                                  Go live
                                </button>
                              )}
                              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                                disabled={setEnabled.isPending}
                                onClick={() => void step.run(() =>
                                  setEnabled.mutateAsync({ key: j.key, is_enabled: !j.is_enabled }))}>
                                {j.is_enabled ? 'Disable' : 'Enable'}
                              </button>
                            </span>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr key={`${j.key}-detail`}>
                          <td colSpan={6} style={{ background: 'var(--arc-surface-2)' }}>
                            {j.last_error && (
                              <p style={{ color: 'var(--arc-danger)', fontSize: 12.5, marginTop: 0 }}>
                                {j.last_error}
                              </p>
                            )}
                            <div className="arc-label">Recent runs</div>
                            {runs.filter((r) => r.job_key === j.key).length === 0 ? (
                              <p className="arc-subtle" style={{ fontSize: 12 }}>
                                No runs recorded.
                              </p>
                            ) : (
                              <ul style={{ margin: '6px 0 0', padding: 0, listStyle: 'none' }}>
                                {runs.filter((r) => r.job_key === j.key).slice(0, 8).map((r, i) => (
                                  <li key={i} className="arc-row"
                                    style={{ gap: 10, fontSize: 12, padding: '3px 0' }}>
                                    <span className="arc-subtle" style={{ width: 90 }}>
                                      {relativeTime(r.started_at)}
                                    </span>
                                    <span style={r.status === 'failed'
                                      ? { color: 'var(--arc-danger)', fontWeight: 550, width: 64 }
                                      : { width: 64 }}>
                                      {/* "skipped" is not a failure. It is the
                                          job running and finding nothing to do,
                                          which is what most runs of most jobs
                                          are — and it is proof it ran. */}
                                      {r.status === 'skipped' ? 'nothing to do' : r.status}
                                    </span>
                                    <span className="arc-subtle arc-num" style={{ width: 60 }}>
                                      {r.duration_ms != null ? `${r.duration_ms}ms` : ''}
                                    </span>
                                    <span className="arc-subtle arc-mono arc-truncate"
                                      style={{ flex: 1, fontSize: 11 }}>
                                      {r.error ?? (r.detail ? JSON.stringify(r.detail) : '')}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
